// Regels die Opschonen en Mijn werk delen (proto-opschonen-logic.js, proto-mijnwerk-logic.js): parkeren met het label
// On hold, oppakken en terug naar het actieve werk door labels weg te halen, een nieuwe deadline, en wat "open" is.
import { ddmm, diffDays, dow, meaningOf, todayIn } from "./planning";
import type { Label, PlanningData, Task, TaskLabel, TaskOp } from "./types";

/** Afgewerkt (voortgang 1) maar Planner verwerkt het nog: niet meer tonen; Ongedaan maken brengt de taak terug. */
export const isOpen = (t: Task) => t.progress < 1;

/** De labels die setLabelOps weghaalt: de andere statuslabels van de taak; bij een label op "negeren" geen. */
export const replacedBy = (t: Task, label: Pick<Label, "id" | "name">): TaskLabel[] =>
  meaningOf(label.name) === "negeren" ? [] : t.labels.filter((l) => l.id !== label.id && meaningOf(l.name) !== "negeren");

/**
 * Een statuslabel zetten als enige: de andere labels met een betekenis (actief, geparkeerd, afsluiten) gaan eraf, labels
 * op "negeren" blijven. Zo staat in Planner na een wijziging uit de app niet Prod naast On hold of Test.
 */
export const setLabelOps = (t: Task, label: Pick<Label, "id" | "name">): TaskOp[] =>
  [{ kind: "label", labelId: label.id, on: true }, ...replacedBy(t, label).map((l): TaskOp => ({ kind: "label", labelId: l.id, on: false }))];

/** Het label om te parkeren in het plan van deze taak: On hold, anders Waiting; null als het plan geen van beide kent. */
export function parkLabel(t: Task, data: Pick<PlanningData, "labels">): Label | null {
  const own = data.labels.filter((l) => l.projectId === t.projectId);
  const named = (n: string) => own.find((l) => l.name.trim().toLowerCase() === n);
  return named("on hold") || named("waiting") || null;
}

export function parkOps(t: Task, data: Pick<PlanningData, "labels">): TaskOp[] | null {
  const l = parkLabel(t, data);
  return l ? setLabelOps(t, l) : null;
}

/** Oppakken: de labels die de taak parkeren weghalen. */
export const resumeOps = (t: Task): TaskOp[] => t.labels.filter((l) => meaningOf(l.name) === "geparkeerd").map((l) => ({ kind: "label", labelId: l.id, on: false }));

/** Terug naar het actieve werk: de labels die de taak klaar om af te sluiten maken weghalen. */
export const reopenOps = (t: Task): TaskOp[] => t.labels.filter((l) => meaningOf(l.name) === "afsluiten").map((l) => ({ kind: "label", labelId: l.id, on: false }));

/** Nieuwe deadline; een start die erna valt, schuift mee (anders weigert Planner). */
export const endOps = (t: Task, end: string): TaskOp[] => [t.start > end ? { kind: "dates", start: end, end } : { kind: "dates", end }];

export const count = (n: number) => (n === 1 ? "1 taak" : n + " taken");

const DAYS = ["maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag", "zondag"];
/** "vrijdag 16/10" */
export const dayDate = (s: string) => DAYS[dow(s)] + " " + ddmm(s);

/** Wanneer iets gebeurde: "vandaag 08:55", "gisteren 16:40" of "28/09 10:12". */
export function when(iso: string, today: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const day = todayIn("Europe/Brussels", at);
  const hm = new Intl.DateTimeFormat("nl-BE", { timeZone: "Europe/Brussels", hour: "2-digit", minute: "2-digit" }).format(at);
  const ago = diffDays(day, today);
  return (ago === 0 ? "vandaag" : ago === 1 ? "gisteren" : ddmm(day)) + " " + hm;
}
